// FILE: src/pages/AccountDetailPage.jsx
//
// Account detail — redesigned in the app's modern design language
// (scoped CSS under .adpg-root, Geist typography, sticky header, cards).
// All behavior preserved from the previous version:
//   - GET /accounts/detail/:id  → { account, recentTxns?, recentRecons? }
//   - GET /users/me             → currency preference
//   - PUT /accounts/:id         → edit (surfaces res.data.warning) + archive toggle
//   - navigate("/cash-accounts") back link
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import API from "../api";
import { formatMoney } from "../utils/formatMoney";

const ACCOUNT_DETAIL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

/* Dialog papers reuse .adpg-root for tokens — never inherit page sizing */
.MuiPaper-root.adpg-root {
  min-height: 0;
  background: #fff;
}
.adpg-root {
  --adpg-primary: #1E90FF;
  --adpg-primary-soft: #E8F3FF;
  --adpg-warn: #FFC107;
  --adpg-warn-soft: #FFF7DC;
  --adpg-warn-deep: #B86E00;
  --adpg-success: #2E7D32;
  --adpg-success-soft: #E8F5E9;
  --adpg-danger: #C62828;
  --adpg-danger-soft: #FDECEC;
  --adpg-text-1: #111827;
  --adpg-text-2: #374151;
  --adpg-text-3: #6B7280;
  --adpg-text-4: #9CA3AF;
  --adpg-border-1: #ECECEC;
  --adpg-border-2: #DDDDDD;
  --adpg-surface: #FFFFFF;
  --adpg-surface-sub: #FAFAFA;
  --adpg-surface-sub-2: #F4F4F5;
  --adpg-radius-sm: 6px;
  --adpg-radius-md: 8px;
  --adpg-radius-lg: 12px;
  --adpg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --adpg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--adpg-text-1);
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.adpg-root * { box-sizing: border-box; }
.adpg-root.adpg-page {
  background: var(--adpg-surface-sub);
  min-height: 100vh;
  padding: 0;
}

/* ----- Sticky top ----- */
.adpg-sticky-top {
  position: sticky;
  top: var(--adpg-navbar-h);
  z-index: 30;
  background: var(--adpg-surface-sub);
  border-bottom: 1px solid var(--adpg-border-1);
}
.adpg-header {
  padding: 16px 24px 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}
.adpg-title-block { min-width: 0; flex: 1 1 320px; }
.adpg-back {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; padding: 0;
  color: var(--adpg-text-3); font-family: inherit; font-size: 12px;
  cursor: pointer; margin-bottom: 6px;
}
.adpg-back:hover { color: var(--adpg-primary); }
.adpg-title {
  font-size: 22px; font-weight: 600; letter-spacing: -0.02em;
  margin: 0; color: var(--adpg-text-1);
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.adpg-title-no {
  font-family: 'Geist Mono', monospace;
  font-size: 12.5px; font-weight: 500; color: var(--adpg-text-3);
}
.adpg-subtitle { font-size: 12.5px; color: var(--adpg-text-3); margin-top: 3px; }

.adpg-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.adpg-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--adpg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease; white-space: nowrap;
}
.adpg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.adpg-btn-primary { background: var(--adpg-primary); color: #fff; }
.adpg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.adpg-btn-ghost {
  background: var(--adpg-surface); color: var(--adpg-text-2);
  border-color: var(--adpg-border-2);
}
.adpg-btn-ghost:hover:not(:disabled) { background: var(--adpg-surface-sub-2); }
.adpg-btn-warn-soft {
  background: var(--adpg-warn-soft); color: var(--adpg-warn-deep);
  border-color: #F1D88A;
}
.adpg-btn-warn-soft:hover:not(:disabled) { background: #FFF0C2; }
.adpg-btn-success-soft {
  background: var(--adpg-success-soft); color: var(--adpg-success);
  border-color: #B7DBBA;
}
.adpg-btn-success-soft:hover:not(:disabled) { background: #DCF0DD; }

/* ----- Body ----- */
.adpg-body { padding: 18px 24px 32px; max-width: 980px; margin: 0 auto; }

/* Hero card */
.adpg-hero {
  background: var(--adpg-surface);
  border: 1px solid var(--adpg-border-1);
  border-radius: var(--adpg-radius-lg);
  box-shadow: var(--adpg-shadow-sm);
  padding: 18px 20px;
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 20px; flex-wrap: wrap;
  margin-bottom: 14px;
}
.adpg-hero-lab {
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--adpg-text-3); margin-bottom: 2px;
}
.adpg-hero-balance {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 30px; font-weight: 600; letter-spacing: -0.01em;
  color: var(--adpg-text-1);
}
.adpg-hero-balance.is-neg { color: var(--adpg-danger); }
.adpg-chips { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }
.adpg-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 2px 9px 2px 7px;
  font-size: 11px; font-weight: 500; border-radius: 999px; white-space: nowrap;
}
.adpg-chip-dot { width: 6px; height: 6px; border-radius: 50%; }
.adpg-chip-active { background: var(--adpg-success-soft); color: var(--adpg-success); border: 1px solid #B7DBBA; }
.adpg-chip-active .adpg-chip-dot { background: var(--adpg-success); }
.adpg-chip-archived { background: var(--adpg-surface-sub-2); color: var(--adpg-text-3); border: 1px solid var(--adpg-border-2); }
.adpg-chip-archived .adpg-chip-dot { background: var(--adpg-text-4); }
.adpg-chip-locked { background: var(--adpg-warn-soft); color: var(--adpg-warn-deep); border: 1px solid #F1D88A; }
.adpg-chip-locked .adpg-chip-dot { background: var(--adpg-warn-deep); }
.adpg-chip-reopen { background: var(--adpg-primary-soft); color: #0E5EAB; border: 1px solid #BBDCFB; }
.adpg-chip-reopen .adpg-chip-dot { background: var(--adpg-primary); }
.adpg-chip-alert { background: var(--adpg-danger-soft); color: var(--adpg-danger); border: 1px solid #F4C7C7; }
.adpg-chip-alert .adpg-chip-dot { background: var(--adpg-danger); }

/* Info cards grid */
.adpg-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  margin-bottom: 14px;
}
@media (max-width: 760px) { .adpg-grid { grid-template-columns: 1fr; } }
.adpg-card {
  background: var(--adpg-surface);
  border: 1px solid var(--adpg-border-1);
  border-radius: var(--adpg-radius-lg);
  box-shadow: var(--adpg-shadow-sm);
  padding: 16px 18px;
}
.adpg-card-head {
  font-size: 11.5px; font-weight: 600; letter-spacing: 0.04em;
  text-transform: uppercase; color: var(--adpg-text-2);
  padding-bottom: 8px; margin-bottom: 12px;
  border-bottom: 1px solid var(--adpg-border-1);
}
.adpg-field { margin-bottom: 10px; }
.adpg-field:last-child { margin-bottom: 0; }
.adpg-field-lab {
  font-size: 10.5px; font-weight: 500; letter-spacing: 0.04em;
  text-transform: uppercase; color: var(--adpg-text-3); margin-bottom: 1px;
}
.adpg-field-val { font-size: 13px; font-weight: 500; color: var(--adpg-text-1); }
.adpg-field-val.mono {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums; font-size: 12.5px;
}
.adpg-field-val.is-empty { color: var(--adpg-text-4); font-style: italic; font-weight: 400; }

/* Recent lists */
.adpg-list-card { margin-bottom: 14px; }
.adpg-list-row {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 8px 10px;
  background: var(--adpg-surface-sub);
  border-radius: var(--adpg-radius-sm);
  font-size: 12.5px;
  margin-bottom: 5px;
}
.adpg-list-row:last-child { margin-bottom: 0; }
.adpg-list-date {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums; color: var(--adpg-text-2);
}
.adpg-list-amt {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums; font-weight: 600; color: var(--adpg-text-1);
}

/* States */
.adpg-state { padding: 64px 32px; text-align: center; color: var(--adpg-text-3); }
.adpg-state-title { color: var(--adpg-text-2); font-size: 14px; font-weight: 500; margin: 12px 0 4px; }
.adpg-error-banner {
  margin: 18px 24px; padding: 10px 14px;
  background: var(--adpg-danger-soft); color: var(--adpg-danger);
  border-left: 3px solid var(--adpg-danger);
  border-radius: var(--adpg-radius-sm); font-size: 13px;
}

/* ----- Dialog skinning ----- */
.adpg-dlg .MuiPaper-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, sans-serif !important;
  border-radius: var(--adpg-radius-lg) !important;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.18) !important;
  overflow: hidden;
}
.adpg-dlg-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--adpg-border-1);
}
.adpg-dlg-title { font-size: 16px; font-weight: 600; color: var(--adpg-text-1); margin: 0; }
.adpg-dlg-sub { font-size: 12px; color: var(--adpg-text-3); margin-top: 3px; }
.adpg-dlg-close {
  background: none; border: none; color: var(--adpg-text-3);
  width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.adpg-dlg-close:hover { background: var(--adpg-surface-sub-2); color: var(--adpg-text-1); }
.adpg-dlg-body { padding: 18px 20px; }
.adpg-dlg-foot {
  padding: 14px 20px;
  border-top: 1px solid var(--adpg-border-1);
  background: var(--adpg-surface-sub);
  display: flex; align-items: center; justify-content: flex-end; gap: 8px;
}
.adpg-flab {
  display: block; font-size: 11px; font-weight: 500;
  color: var(--adpg-text-2); letter-spacing: 0.02em;
  text-transform: uppercase; margin-bottom: 4px;
}
.adpg-flab-req::after { content: ' *'; color: var(--adpg-primary); font-weight: 700; }
.adpg-frow { margin-bottom: 14px; }
.adpg-finput {
  width: 100%; height: 34px; padding: 0 10px;
  border: 1px solid var(--adpg-border-2);
  border-radius: var(--adpg-radius-md);
  font-family: inherit; font-size: 13px;
  color: var(--adpg-text-1); background: #fff;
}
.adpg-finput:focus {
  outline: none; border-color: var(--adpg-primary);
  box-shadow: 0 0 0 3px var(--adpg-primary-soft);
}
.adpg-ftextarea {
  width: 100%; min-height: 64px; padding: 8px 10px; resize: vertical;
  border: 1px solid var(--adpg-border-2); border-radius: var(--adpg-radius-md);
  font-family: inherit; font-size: 13px; color: var(--adpg-text-1); background: #fff;
}
.adpg-ftextarea:focus {
  outline: none; border-color: var(--adpg-primary);
  box-shadow: 0 0 0 3px var(--adpg-primary-soft);
}
`;

function IconBack({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconEdit({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M11.3 2.7a1.6 1.6 0 012.3 2.3L5.5 13.1l-3 .7.7-3 8.1-8.1z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconArchive({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M2 3h12v3H2zM3 6v7h10V6M6.5 9h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconClose({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function AccountDetailPage() {
  const { id } = useParams(); // e.g. /account/detail/:id
  const navigate = useNavigate();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Alerts (shown via bottom-center Snackbar)
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  // Edit dialog state
  const [openEdit, setOpenEdit] = useState(false);
  const [editAccountNo, setEditAccountNo] = useState("");
  const [editAccountName, setEditAccountName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  // OPTIONAL: Recent transactions/reconciliations
  const [recentTxns, setRecentTxns] = useState([]);
  const [recentRecons, setRecentRecons] = useState([]);

  // User's preferred currency (default USD)
  const [currency, setCurrency] = useState("USD");

  useEffect(() => {
    fetchAccountDetails(id);
    // Fetch user currency preference
    fetchUserCurrency();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function fetchAccountDetails(accId) {
    setLoading(true);
    setError("");
    try {
      const res = await API.get(`/accounts/detail/${accId}`);
      setAccount(res.data.account);
      if (res.data.recentTxns) setRecentTxns(res.data.recentTxns);
      if (res.data.recentRecons) setRecentRecons(res.data.recentRecons);

      setAlertMessage("");
    } catch (err) {
      console.error("fetchAccountDetails error:", err);
      setError(err.response?.data?.message || "Failed to load account details");
    } finally {
      setLoading(false);
    }
  }

  async function fetchUserCurrency() {
    try {
      const res = await API.get("/users/me");
      setCurrency(res.data.currency || "USD");
    } catch (err) {
      console.error("Error fetching user currency:", err);
    }
  }

  // --------------------------
  // EDIT ACCOUNT
  // --------------------------
  const handleEditClick = () => {
    if (!account) return;
    setEditAccountNo(account.accountNo || "");
    setEditAccountName(account.accountName || "");
    setEditDescription(account.description || "");
    setOpenEdit(true);
  };

  const handleEditSave = async () => {
    if (!account) return;
    try {
      const res = await API.put(`/accounts/${account._id}`, {
        accountNo: editAccountNo,
        accountName: editAccountName,
        description: editDescription,
      });

      // If there's a warning about duplicate accountNo, show it
      if (res.data.warning) {
        setAlertMessage(res.data.warning);
        setAlertSeverity("warning");
      } else {
        setAlertMessage(res.data.message || "Account updated successfully!");
        setAlertSeverity("success");
      }

      // Update local account state
      setAccount(res.data.account);
      setOpenEdit(false);
    } catch (err) {
      console.error("Edit account error:", err);
      setAlertMessage(err.response?.data?.message || "Failed to edit account");
      setAlertSeverity("error");
    }
  };

  // --------------------------
  // ARCHIVE / UNARCHIVE
  // --------------------------
  const handleArchiveClick = async () => {
    if (!account) return;
    try {
      // Toggle isActive
      const newIsActive = !account.isActive;
      const res = await API.put(`/accounts/${account._id}`, {
        isActive: newIsActive,
      });

      // The server returns updated "account" in res.data.account
      setAccount(res.data.account);

      if (newIsActive) {
        setAlertMessage("Account unarchived successfully!");
      } else {
        setAlertMessage("Account archived successfully!");
      }
      setAlertSeverity("success");
    } catch (err) {
      console.error("Archive/unarchive error:", err);
      setAlertMessage(err.response?.data?.message || "Failed to toggle archive");
      setAlertSeverity("error");
    }
  };

  const createdByLabel = account?.createdByUser
    ? account.createdByUser.displayName || account.createdByUser.username || "—"
    : "";

  // --------------------------
  // RENDER
  // --------------------------
  return (
    <div className="adpg-root adpg-page">
      <style>{ACCOUNT_DETAIL_CSS}</style>

      {loading ? (
        <div className="adpg-state">
          <CircularProgress size={28} />
          <div className="adpg-state-title">Loading account…</div>
        </div>
      ) : error ? (
        <>
          <div className="adpg-error-banner" role="alert">
            {error}
          </div>
          <div style={{ padding: "0 24px" }}>
            <button
              type="button"
              className="adpg-btn adpg-btn-ghost"
              onClick={() => navigate("/cash-accounts")}
            >
              <IconBack /> Back to Accounts
            </button>
          </div>
        </>
      ) : !account ? (
        <div className="adpg-state">
          <div className="adpg-state-title">No account found.</div>
          <button
            type="button"
            className="adpg-btn adpg-btn-ghost"
            style={{ marginTop: 12 }}
            onClick={() => navigate("/cash-accounts")}
          >
            <IconBack /> Back to Accounts
          </button>
        </div>
      ) : (
        <>
          {/* Sticky top */}
          <div className="adpg-sticky-top">
            <div className="adpg-header">
              <div className="adpg-title-block">
                <button
                  type="button"
                  className="adpg-back"
                  onClick={() => navigate("/cash-accounts")}
                >
                  <IconBack /> Back to Accounts
                </button>
                <h1 className="adpg-title">
                  {account.accountName}
                  <span className="adpg-title-no">#{account.accountNo}</span>
                </h1>
                <div className="adpg-subtitle">
                  {account.description || "No description"}
                </div>
              </div>
              <div className="adpg-actions">
                <button
                  type="button"
                  className="adpg-btn adpg-btn-primary"
                  onClick={handleEditClick}
                >
                  <IconEdit /> Edit Account
                </button>
                {account.isActive ? (
                  <button
                    type="button"
                    className="adpg-btn adpg-btn-warn-soft"
                    onClick={handleArchiveClick}
                  >
                    <IconArchive /> Archive
                  </button>
                ) : (
                  <button
                    type="button"
                    className="adpg-btn adpg-btn-success-soft"
                    onClick={handleArchiveClick}
                  >
                    <IconArchive /> Unarchive
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="adpg-body">
            {/* Hero: balance + status chips */}
            <div className="adpg-hero">
              <div>
                <div className="adpg-hero-lab">Balance</div>
                <div
                  className={`adpg-hero-balance ${
                    (account.balance || 0) < 0 ? "is-neg" : ""
                  }`}
                >
                  {formatMoney(account.balance || 0, currency)}
                </div>
              </div>
              <div className="adpg-chips">
                {account.isActive ? (
                  <span className="adpg-chip adpg-chip-active">
                    <span className="adpg-chip-dot" /> Active
                  </span>
                ) : (
                  <span className="adpg-chip adpg-chip-archived">
                    <span className="adpg-chip-dot" /> Archived
                  </span>
                )}
                {account.lockedUpToDate && (
                  <span className="adpg-chip adpg-chip-locked">
                    <span className="adpg-chip-dot" /> Locked Until{" "}
                    {new Date(account.lockedUpToDate).toLocaleDateString()}
                  </span>
                )}
                {account.inReOpenMode && (
                  <span className="adpg-chip adpg-chip-reopen">
                    <span className="adpg-chip-dot" /> ReOpen Mode
                  </span>
                )}
                {(account.notifications || []).map((n) => (
                  <span className="adpg-chip adpg-chip-alert" key={n}>
                    <span className="adpg-chip-dot" /> {n}
                  </span>
                ))}
              </div>
            </div>

            {/* Info cards */}
            <div className="adpg-grid">
              <div className="adpg-card">
                <div className="adpg-card-head">Basic info</div>
                <div className="adpg-field">
                  <div className="adpg-field-lab">Name</div>
                  <div className="adpg-field-val">{account.accountName}</div>
                </div>
                <div className="adpg-field">
                  <div className="adpg-field-lab">Number</div>
                  <div className="adpg-field-val mono">{account.accountNo}</div>
                </div>
                <div className="adpg-field">
                  <div className="adpg-field-lab">Description</div>
                  <div
                    className={`adpg-field-val ${
                      account.description ? "" : "is-empty"
                    }`}
                  >
                    {account.description || "N/A"}
                  </div>
                </div>
              </div>

              <div className="adpg-card">
                <div className="adpg-card-head">Lock &amp; audit</div>
                <div className="adpg-field">
                  <div className="adpg-field-lab">Locked Up To</div>
                  <div
                    className={`adpg-field-val mono ${
                      account.lockedUpToDate ? "" : "is-empty"
                    }`}
                  >
                    {account.lockedUpToDate
                      ? new Date(account.lockedUpToDate).toLocaleDateString()
                      : "N/A"}
                  </div>
                </div>
                <div className="adpg-field">
                  <div className="adpg-field-lab">Created At</div>
                  <div className="adpg-field-val mono">
                    {new Date(account.createdAt).toLocaleString()}
                  </div>
                </div>
                {account.createdByUser && (
                  <div className="adpg-field">
                    <div className="adpg-field-lab">Created By</div>
                    <div className="adpg-field-val">
                      {createdByLabel}
                      {account.createdByUser.role
                        ? ` · ${account.createdByUser.role}`
                        : ""}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Transactions (if any) */}
            {recentTxns.length > 0 && (
              <div className="adpg-card adpg-list-card">
                <div className="adpg-card-head">Recent Transactions</div>
                {recentTxns.map((txn) => (
                  <div className="adpg-list-row" key={txn._id}>
                    <span className="adpg-list-date">
                      {new Date(txn.date).toLocaleDateString()} — {txn.type}
                    </span>
                    <span className="adpg-list-amt">
                      {formatMoney(txn.amount, currency)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Recent Reconciliations (if any) */}
            {recentRecons.length > 0 && (
              <div className="adpg-card adpg-list-card">
                <div className="adpg-card-head">Recent Reconciliations</div>
                {recentRecons.map((rec) => (
                  <div className="adpg-list-row" key={rec._id}>
                    <span className="adpg-list-date">
                      {new Date(rec.date).toLocaleDateString()}
                    </span>
                    <span>Status: {rec.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* EDIT DIALOG */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="sm"
        className="adpg-dlg"
        PaperProps={{ className: "adpg-root" }}
      >
        <style>{ACCOUNT_DETAIL_CSS}</style>
        <div className="adpg-dlg-head">
          <div>
            <div className="adpg-dlg-title">Edit Cash Account</div>
            <div className="adpg-dlg-sub">
              Update the account's number, name, or description.
            </div>
          </div>
          <button
            className="adpg-dlg-close"
            onClick={() => setOpenEdit(false)}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>
        <DialogContent dividers className="adpg-dlg-body">
          <div className="adpg-frow">
            <label className="adpg-flab adpg-flab-req" htmlFor="adpg-edit-no">
              Account No.
            </label>
            <input
              id="adpg-edit-no"
              type="text"
              className="adpg-finput"
              value={editAccountNo}
              onChange={(e) => setEditAccountNo(e.target.value)}
            />
          </div>
          <div className="adpg-frow">
            <label className="adpg-flab adpg-flab-req" htmlFor="adpg-edit-name">
              Account Name
            </label>
            <input
              id="adpg-edit-name"
              type="text"
              className="adpg-finput"
              value={editAccountName}
              onChange={(e) => setEditAccountName(e.target.value)}
            />
          </div>
          <div className="adpg-frow" style={{ marginBottom: 0 }}>
            <label className="adpg-flab" htmlFor="adpg-edit-desc">
              Description (Optional)
            </label>
            <textarea
              id="adpg-edit-desc"
              className="adpg-ftextarea"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
          </div>
        </DialogContent>
        <div className="adpg-dlg-foot">
          <button
            type="button"
            className="adpg-btn adpg-btn-ghost"
            onClick={() => setOpenEdit(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="adpg-btn adpg-btn-primary"
            onClick={handleEditSave}
          >
            Save Changes
          </button>
        </div>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={Boolean(alertMessage)}
        autoHideDuration={4000}
        onClose={() => setAlertMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setAlertMessage("")}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default AccountDetailPage;
